import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import {
    selectProductDetails,
    selectProductDetailsLoading,
    selectReverseAccessories,
    selectReverseAccessoriesLoading,
    getProductDetails,
    getReverseAccessories,
    resetRelatedProducts,
    updateProduct
} from "../../redux/products/productsSlice";
import { closeModal, setModal } from "../../redux/rightModalSlice";

// Card prodotto riutilizzabile per correlati e reverse
function ProductCard({ product, onClick, formatPrice }) {
    const productId = product._id || product.id;
    const productMedia = (product.media || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    const cover = productMedia.find(m => m.cover && m.kind?.startsWith('image/'))
        || productMedia.find(m => m.kind?.startsWith('image/'))
        || null;

    return (
        <button
            key={productId}
            onClick={() => onClick(productId)}
            className="text-left border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
        >
            <div className="w-full h-28 bg-gray-50 flex items-center justify-center">
                {cover ? (
                    <img
                        src={cover.url}
                        alt={product.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <DynamicIcon iconClass="fa-solid fa-image text-2xl text-gray-300" />
                )}
            </div>
            <div className="p-2">
                <p className="text-sm font-medium text-gray-800 truncate">{product.title || '--'}</p>
                <p className="text-xs text-gray-400 truncate">{product.code || ''}</p>
                <p className="text-sm font-bold text-blue-700 mt-1">{formatPrice(product.sellPrice)}</p>
            </div>
        </button>
    );
}

export default function ProductPreview() {
    const dispatch = useDispatch();
    const productDetails = useSelector(selectProductDetails);
    const loading = useSelector(selectProductDetailsLoading);
    const reverseAccessories = useSelector(selectReverseAccessories);
    const reverseAccessoriesLoading = useSelector(selectReverseAccessoriesLoading);
    const token = localStorage.getItem('installamelo-auth-token');

    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
    const [deletingMedia, setDeletingMedia] = useState(false);

    const formatPrice = (price) => {
        if (price === undefined || price === null) return "--";
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
    };

    const handleEditClick = () => {
        dispatch(setModal({ mode: 'edit', id: productDetails._id }));
    };

    // Elimina un'immagine dal prodotto
    const handleDeleteMedia = async (mediaItem) => {
        if (!window.confirm('Eliminare questa immagine?')) return;
        setDeletingMedia(true);
        const updatedMedia = (productDetails.media || [])
            .filter(m => m.url !== mediaItem.url)
            .map((m, i) => ({ ...m, order: i }));
        // Se l'immagine eliminata era la copertina, assegno la copertina alla prima
        if (mediaItem.cover && updatedMedia.length > 0) {
            updatedMedia[0] = { ...updatedMedia[0], cover: true };
        }
        const result = await dispatch(updateProduct({
            product: { _id: productDetails._id, media: updatedMedia },
            token
        }));
        if (!result.error) {
            // Aggiorno l'indice selezionato se necessario
            if (selectedMediaIndex >= updatedMedia.filter(m => m.kind?.startsWith('image/')).length) {
                setSelectedMediaIndex(Math.max(0, updatedMedia.filter(m => m.kind?.startsWith('image/')).length - 1));
            }
            dispatch(getProductDetails({ productId: productDetails._id, token }));
        }
        setDeletingMedia(false);
    };

    // Apre la preview di un prodotto correlato
    const handleProductClick = (targetProductId) => {
        setSelectedMediaIndex(0);
        dispatch(resetRelatedProducts());
        dispatch(getProductDetails({ productId: targetProductId, token }));
        dispatch(getReverseAccessories({ productId: targetProductId, token }));
        dispatch(setModal({ mode: 'preview', id: targetProductId }));
    };

    if (loading) {
        return <LoadingSection />;
    }

    if (!productDetails) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Prodotto non trovato</h2>
                <p className="text-gray-600">Il prodotto richiesto non e' disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }

    // Media e copertina
    const media = (productDetails.media || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    const imageMedia = media.filter(m => m.kind && m.kind.startsWith('image/'));
    const currentImage = imageMedia[selectedMediaIndex] || null;

    // Categorie del prodotto
    const categories = productDetails.categories && productDetails.categories.length > 0
        ? productDetails.categories
        : productDetails.categoryId
            ? [{ categoryId: productDetails.categoryId, categoryName: productDetails.categoryName }]
            : [];

    // Correlazioni configurate (linkedAccessories) - direzione forward
    const linkedAccessories = productDetails.linkedAccessories || [];

    // Markup badge color
    const getMarkupColor = (markup) => {
        if (!markup) return 'bg-gray-100 text-gray-600';
        if (markup >= 50) return 'bg-green-100 text-green-700';
        if (markup >= 20) return 'bg-blue-100 text-blue-700';
        return 'bg-yellow-100 text-yellow-700';
    };

    // Prodotti correlati (chi ha questo prodotto o le sue categorie nei propri linkedAccessories)
    const filteredReverse = (reverseAccessories || []).filter(
        p => (p._id || p.id) !== productDetails._id
    );

    return (
        <div className="p-6 overflow-y-auto">
            {/* Header con titolo e bottone chiudi */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-2xl font-bold">{productDetails.title || "Prodotto"}</h2>
                    <div className="flex items-center gap-3 mt-1">
                        {productDetails.code && (
                            <span className="text-sm text-gray-500">Codice: {productDetails.code}</span>
                        )}
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                            productDetails.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                            {productDetails.active ? 'Attivo' : 'Inattivo'}
                        </span>
                    </div>
                </div>
                <button
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={() => dispatch(closeModal())}
                    title="Chiudi"
                >
                    <DynamicIcon iconClass="fa-solid fa-times text-gray-500 text-lg" />
                </button>
            </div>

            {/* Sezione principale: galleria + prezzi */}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
                {/* Galleria immagini */}
                <div className="md:w-1/2">
                    {imageMedia.length > 0 ? (
                        <>
                            {/* Immagine principale */}
                            <div className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50 mb-3">
                                <img
                                    src={currentImage?.url}
                                    alt={currentImage?.fileName || productDetails.title}
                                    className="w-full h-72 object-contain"
                                />
                                {currentImage && (
                                    <button
                                        onClick={() => handleDeleteMedia(currentImage)}
                                        disabled={deletingMedia}
                                        className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                                        title="Elimina immagine"
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-trash text-xs" />
                                    </button>
                                )}
                            </div>
                            {/* Thumbnails */}
                            {imageMedia.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                    {imageMedia.map((item, index) => (
                                        <div key={item.url || index} className="relative group/thumb flex-shrink-0">
                                            <button
                                                onClick={() => setSelectedMediaIndex(index)}
                                                className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                                                    index === selectedMediaIndex
                                                        ? 'border-blue-500'
                                                        : 'border-gray-200 hover:border-gray-400'
                                                }`}
                                            >
                                                <img
                                                    src={item.url}
                                                    alt={item.fileName || `Immagine ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMedia(item)}
                                                disabled={deletingMedia}
                                                className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white opacity-0 group-hover/thumb:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                                                title="Elimina"
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-times text-[10px]" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="border border-gray-200 rounded-lg bg-gray-50 h-72 flex items-center justify-center">
                            <div className="text-center text-gray-300">
                                <DynamicIcon iconClass="fa-solid fa-image text-5xl mb-2" />
                                <p className="text-sm">Nessuna immagine</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sezione prezzi */}
                <div className="md:w-1/2">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Prezzi</h3>

                        <div className="space-y-3">
                            {/* Prezzo di acquisto */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Prezzo Acquisto</span>
                                <span className="text-sm font-medium">{formatPrice(productDetails.purchasePrice)}</span>
                            </div>

                            {/* Markup */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Markup</span>
                                <span className={`px-2 py-0.5 rounded-full text-sm font-semibold ${getMarkupColor(productDetails.markup)}`}>
                                    {productDetails.markup ? `+${productDetails.markup}%` : '--'}
                                </span>
                            </div>

                            {/* Separatore */}
                            <hr className="border-blue-200" />

                            {/* Prezzo di vendita - in evidenza */}
                            <div className="flex justify-between items-center">
                                <span className="text-base font-semibold text-gray-800">Prezzo di Vendita</span>
                                <span className="text-2xl font-bold text-blue-700">{formatPrice(productDetails.sellPrice)}</span>
                            </div>

                            {/* Prezzo di listino */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Prezzo di Listino</span>
                                <span className="text-sm font-medium">{formatPrice(productDetails.listPrice)}</span>
                            </div>

                            {/* IVA */}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">IVA</span>
                                <span className="text-sm font-medium">
                                    {productDetails.vatLabel || (productDetails.vatValue !== undefined && productDetails.vatValue !== "" ? `${productDetails.vatValue}%` : "--")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Descrizione */}
            {productDetails.description && (
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Descrizione</h3>
                    <p className="text-gray-700 leading-relaxed">{productDetails.description}</p>
                </div>
            )}

            {/* Dettagli */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Dettagli</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    {productDetails.supplierLabel && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Fornitore</span>
                            <span className="font-medium">{productDetails.supplierLabel}</span>
                        </div>
                    )}
                    {productDetails.supplierCode && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Cod. Fornitore</span>
                            <span className="font-medium">{productDetails.supplierCode}</span>
                        </div>
                    )}
                    {productDetails.unitaLabel && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Unita' di Acquisto</span>
                            <span className="font-medium">{productDetails.unitaLabel}</span>
                        </div>
                    )}
                    {productDetails.warehouseName && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Magazzino</span>
                            <span className="font-medium">{productDetails.warehouseName}</span>
                        </div>
                    )}
                    {productDetails.warehousePosition && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Posizione</span>
                            <span className="font-medium">{productDetails.warehousePosition}</span>
                        </div>
                    )}
                    {productDetails.serialNumber && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Matricola</span>
                            <span className="font-medium">{productDetails.serialNumber}</span>
                        </div>
                    )}
                    {productDetails.batchNumber && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Lotto</span>
                            <span className="font-medium">{productDetails.batchNumber}</span>
                        </div>
                    )}
                    {productDetails.expiryDate && (
                        <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-500">Scadenza</span>
                            <span className="font-medium">{new Date(productDetails.expiryDate).toLocaleDateString('it-IT')}</span>
                        </div>
                    )}
                </div>

                {/* Categorie del prodotto */}
                {categories.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-sm text-gray-500 mr-2">Categorie:</span>
                        {categories.map((cat) => (
                            <span
                                key={cat.categoryId}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1.5 mb-1"
                            >
                                <DynamicIcon iconClass="fa-solid fa-tag text-[10px] mr-1" />
                                {cat.categoryName}
                            </span>
                        ))}
                    </div>
                )}

                {/* Correlazioni configurate (linkedAccessories) */}
                {linkedAccessories.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-sm text-gray-500 mr-2">Accessori configurati:</span>
                        {linkedAccessories.map((acc, index) => (
                            <span
                                key={`${acc.referenceId}-${index}`}
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-1.5 mb-1 ${
                                    acc.type === 'category'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-green-100 text-green-800'
                                }`}
                            >
                                <DynamicIcon iconClass={`fa-solid ${acc.type === 'category' ? 'fa-folder' : 'fa-box'} text-[10px] mr-1`} />
                                {acc.referenceLabel}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Prodotti correlati (prodotti che hanno QUESTO prodotto o le sue categorie nei propri linkedAccessories) */}
            {reverseAccessoriesLoading && (
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Prodotti Correlati</h3>
                    <div className="flex justify-center py-8">
                        <DynamicIcon iconClass="fa-solid fa-spinner fa-spin text-2xl text-gray-400" />
                    </div>
                </div>
            )}

            {!reverseAccessoriesLoading && filteredReverse.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Prodotti Correlati</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {filteredReverse.map((revProduct) => (
                            <ProductCard
                                key={revProduct._id || revProduct.id}
                                product={revProduct}
                                onClick={handleProductClick}
                                formatPrice={formatPrice}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
                <button className="primary" onClick={handleEditClick}>
                    <DynamicIcon iconClass="fas fa-edit" />
                    Modifica
                </button>
            </div>
        </div>
    );
}
